import React, { useState, useEffect } from "react";
import {
  getAllInsuranceRates,
  createInsuranceRate,
  updateInsuranceRate,
  deleteInsuranceRate,
  ensureDefaultFAKRate,
  ensureDefaultSUVARate,
  ensureDefaultKTGRate,
  ensureDefaultPKRate,
  INSURANCE_TYPES,
} from "../services/azureInsuranceService";
import { PK_DEFAULT_ALTERSTABELLE_M, PK_DEFAULT_ALTERSTABELLE_F } from "../utils/insuranceCalculations";

const ZAHLUNGSRHYTHMUS_OPTIONS = [
  { value: "monatlich", label: "Monatlich" },
  { value: "quartalsweise", label: "Quartalsweise" },
  { value: "jaehrlich", label: "Jährlich" },
];

const MONTH_NAMES = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

// Default GueltigAb for new entries: Jan 1 of next year
const defaultGueltigAb = () => {
  const year = new Date().getFullYear() + 1;
  return `${year}-01-01`;
};

const today = () => new Date().toISOString().split("T")[0];

// Empty form data per type
const emptyFormData = (type) => {
  const base = {
    gueltigAb: defaultGueltigAb(),
    zahlungsrhythmus: "monatlich",
    jaehrlichEndMonat: 12,
    bemerkung: "",
  };
  switch (type) {
    case "ahv": return { ...base, satzAN: "", satzAG: "" };
    case "fak": return { ...base, satzAG: "", kinderzulage: "275", ausbildungszulage: "325", minEinkommenMonat: "630" };
    case "suva": return { ...base, nbuAN_M: "", nbuAN_W: "", nbuAG_M: "", nbuAG_W: "", buAG_M: "", buAG_W: "", maxVersicherterLohn: "148200", zahlungsrhythmus: "quartalsweise" };
    case "ktg": return { ...base, satzAN_M: "", satzAN_W: "", satzAG_M: "", satzAG_W: "", maxVersicherterLohn: "148200", zahlungsrhythmus: "quartalsweise" };
    case "pk": return {
      ...base,
      berechnungsModus: "formel",
      koordinationsabzug: "26460",
      eintrittsschwelle: "22680",
      maxVersicherterLohn: "90720",
      anteilAG: "60",
      alterstabelleMaenner: PK_DEFAULT_ALTERSTABELLE_M.map((row) => ({ ...row })),
      alterstabelleFrauen: PK_DEFAULT_ALTERSTABELLE_F.map((row) => ({ ...row })),
    };
    default: return base;
  }
};

// Convert rate entry to form data for editing
const rateToFormData = (entry) => {
  const form = { ...entry };
  delete form.id;
  delete form.type;
  delete form.createdAt;
  delete form.updatedAt;
  return form;
};

const InsuranceSettingsModal = ({ onClose, onRatesChanged }) => {
  const [allRates, setAllRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Editing state: { type, id (null=new), formData }
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    setLoading(true);
    try {
      await Promise.all([
        ensureDefaultFAKRate(),
        ensureDefaultSUVARate(),
        ensureDefaultKTGRate(),
        ensureDefaultPKRate(),
      ]);
      const rates = await getAllInsuranceRates();
      setAllRates(rates);
    } catch {
      setError("Fehler beim Laden der Versicherungsdaten");
    } finally {
      setLoading(false);
    }
  };

  const handleNewEntry = (type) => {
    setEditing({ type, id: null, formData: emptyFormData(type) });
    setError(null);
  };

  const handleEdit = (entry) => {
    setEditing({ type: entry.type, id: entry.id, formData: rateToFormData(entry) });
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setError(null);
  };

  const handleFormChange = (field, value) => {
    setEditing((prev) => ({
      ...prev,
      formData: { ...prev.formData, [field]: value },
    }));
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      if (editing.id) {
        await updateInsuranceRate(editing.type, editing.id, editing.formData);
      } else {
        await createInsuranceRate(editing.type, editing.formData);
      }
      setEditing(null);
      await loadRates();
      if (onRatesChanged) onRatesChanged();
    } catch {
      setError("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (entry.gueltigAb <= today()) return; // Only future entries
    if (!confirm(`Versicherungssatz vom ${entry.gueltigAb} löschen?`)) return;
    try {
      await deleteInsuranceRate(entry.type, entry.id);
      await loadRates();
      if (onRatesChanged) onRatesChanged();
    } catch {
      setError("Fehler beim Löschen");
    }
  };

  // PK Alterstabelle sub-modal state
  const [pkSubModal, setPkSubModal] = useState(null); // null or "maenner" or "frauen"

  // PK Alterstabelle handlers (new format: [{alter, satz}])
  const handleAltersTabelleChange = (geschlecht, rowIdx, field, value) => {
    const tableKey = geschlecht === "maenner" ? "alterstabelleMaenner" : "alterstabelleFrauen";
    setEditing((prev) => {
      const table = [...(prev.formData[tableKey] || [])];
      table[rowIdx] = { ...table[rowIdx], [field]: parseFloat(value) || 0 };
      return { ...prev, formData: { ...prev.formData, [tableKey]: table } };
    });
  };

  const handleAltersTabelleAdd = (geschlecht) => {
    const tableKey = geschlecht === "maenner" ? "alterstabelleMaenner" : "alterstabelleFrauen";
    setEditing((prev) => {
      const table = [...(prev.formData[tableKey] || [])];
      const lastAlter = table.length > 0 ? table[table.length - 1].alter + 1 : 25;
      table.push({ alter: lastAlter, satz: 0 });
      return { ...prev, formData: { ...prev.formData, [tableKey]: table } };
    });
  };

  const handleAltersTabelleRemove = (geschlecht, rowIdx) => {
    const tableKey = geschlecht === "maenner" ? "alterstabelleMaenner" : "alterstabelleFrauen";
    setEditing((prev) => {
      const table = (prev.formData[tableKey] || []).filter((_, i) => i !== rowIdx);
      return { ...prev, formData: { ...prev.formData, [tableKey]: table } };
    });
  };

  // Group rates by type
  const ratesByType = {};
  for (const type of Object.keys(INSURANCE_TYPES)) {
    ratesByType[type] = allRates.filter((r) => r.type === type);
  }

  const renderRateFields = (entry) => {
    switch (entry.type) {
      case "ahv":
        return <span>AN: {entry.satzAN}% / AG: {entry.satzAG}%</span>;
      case "fak":
        return (
          <span>
            AG: {entry.satzAG}% / Kinderzulage: {entry.kinderzulage} CHF / Ausbildungszulage: {entry.ausbildungszulage} CHF / Min: {entry.minEinkommenMonat} CHF
          </span>
        );
      case "suva":
        return (
          <div className="insurance-rate-table">
            <table className="rate-summary-table">
              <thead>
                <tr><th></th><th>M</th><th>W</th></tr>
              </thead>
              <tbody>
                <tr><td>NBU AN</td><td>{entry.nbuAN_M}%</td><td>{entry.nbuAN_W}%</td></tr>
                <tr><td>NBU AG</td><td>{entry.nbuAG_M}%</td><td>{entry.nbuAG_W}%</td></tr>
                <tr><td>BU AG</td><td>{entry.buAG_M}%</td><td>{entry.buAG_W}%</td></tr>
              </tbody>
            </table>
            <span className="rate-cap">Max. Lohn: {new Intl.NumberFormat("de-CH").format(entry.maxVersicherterLohn)} CHF/Jahr</span>
          </div>
        );
      case "ktg":
        return (
          <div className="insurance-rate-table">
            <table className="rate-summary-table">
              <thead>
                <tr><th></th><th>M</th><th>W</th></tr>
              </thead>
              <tbody>
                <tr><td>AN</td><td>{entry.satzAN_M}%</td><td>{entry.satzAN_W}%</td></tr>
                <tr><td>AG</td><td>{entry.satzAG_M}%</td><td>{entry.satzAG_W}%</td></tr>
              </tbody>
            </table>
            <span className="rate-cap">Max. Lohn: {new Intl.NumberFormat("de-CH").format(entry.maxVersicherterLohn)} CHF/Jahr</span>
          </div>
        );
      case "pk": {
        if (entry.berechnungsModus === "manuell") {
          return (
            <div className="insurance-rate-table">
              <span>Berechnung: Manuell pro Person — MA- und AG-Beitrag werden im Team-Tab pro Person erfasst.</span>
            </div>
          );
        }
        const tM = entry.alterstabelleMaenner || entry.alterstabelle || [];
        const tF = entry.alterstabelleFrauen || [];
        return (
          <div className="insurance-rate-table">
            <span>Berechnung: Formel</span>
            <span> / Koord.abzug: {new Intl.NumberFormat("de-CH").format(entry.koordinationsabzug)} CHF</span>
            <span> / Schwelle: {new Intl.NumberFormat("de-CH").format(entry.eintrittsschwelle)} CHF</span>
            <span> / Max. Lohn: {new Intl.NumberFormat("de-CH").format(entry.maxVersicherterLohn || 0)} CHF</span>
            <span> / AG-Anteil: {entry.anteilAG || 50}%</span>
            <span> / M: {tM.length} Stufen, W: {tF.length} Stufen</span>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const renderFormFields = () => {
    if (!editing) return null;
    const { type, formData } = editing;

    const numInput = (label, field) => (
      <div className="form-group" key={field}>
        <label>{label}</label>
        <input
          type="number"
          step="0.01"
          value={formData[field]}
          onChange={(e) => handleFormChange(field, e.target.value)}
        />
      </div>
    );

    switch (type) {
      case "ahv":
        return (
          <div className="form-row">
            {numInput("AN-Satz (%)", "satzAN")}
            {numInput("AG-Satz (%)", "satzAG")}
          </div>
        );
      case "fak":
        return (
          <>
            <div className="form-row">
              {numInput("AG-Satz (%)", "satzAG")}
              {numInput("Min. Einkommen (CHF)", "minEinkommenMonat")}
            </div>
            <div className="form-row">
              {numInput("Kinderzulage (CHF)", "kinderzulage")}
              {numInput("Ausbildungszulage (CHF)", "ausbildungszulage")}
            </div>
          </>
        );
      case "suva":
        return (
          <>
            <div className="form-row">
              {numInput("NBU AN M (%)", "nbuAN_M")}
              {numInput("NBU AN W (%)", "nbuAN_W")}
            </div>
            <div className="form-row">
              {numInput("NBU AG M (%)", "nbuAG_M")}
              {numInput("NBU AG W (%)", "nbuAG_W")}
            </div>
            <div className="form-row">
              {numInput("BU AG M (%)", "buAG_M")}
              {numInput("BU AG W (%)", "buAG_W")}
            </div>
            <div className="form-row">
              {numInput("Max. versicherter Lohn (CHF/Jahr)", "maxVersicherterLohn")}
            </div>
          </>
        );
      case "ktg":
        return (
          <>
            <div className="form-row">
              {numInput("AN M (%)", "satzAN_M")}
              {numInput("AN W (%)", "satzAN_W")}
            </div>
            <div className="form-row">
              {numInput("AG M (%)", "satzAG_M")}
              {numInput("AG W (%)", "satzAG_W")}
            </div>
            <div className="form-row">
              {numInput("Max. versicherter Lohn (CHF/Jahr)", "maxVersicherterLohn")}
            </div>
          </>
        );
      case "pk": {
        const tabelleM = formData.alterstabelleMaenner || formData.alterstabelle || [];
        const tabelleF = formData.alterstabelleFrauen || [];
        const warnM = tabelleM.length > 0 && tabelleM.length < 5;
        const warnF = tabelleF.length > 0 && tabelleF.length < 5;
        const isManuell = formData.berechnungsModus === "manuell";
        return (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Berechnung</label>
                <select
                  value={formData.berechnungsModus || "formel"}
                  onChange={(e) => handleFormChange("berechnungsModus", e.target.value)}
                >
                  <option value="formel">Formel (Alterstabelle)</option>
                  <option value="manuell">Manuell eingeben (pro Person)</option>
                </select>
              </div>
            </div>
            {isManuell ? (
              <div className="pk-manuell-info">
                MA- und AG-Beitrag werden pro Person im Team-Tab (Personendetail) erfasst.
                Fehlende oder veraltete Beiträge werden der GL im Team-Tab angezeigt.
              </div>
            ) : (
              <>
                <div className="form-row">
                  {numInput("Koordinationsabzug (CHF)", "koordinationsabzug")}
                  {numInput("Eintrittsschwelle (CHF)", "eintrittsschwelle")}
                </div>
                <div className="form-row">
                  {numInput("Max. versicherter Lohn (CHF/Jahr)", "maxVersicherterLohn")}
                  {numInput("AG-Anteil (%)", "anteilAG")}
                </div>
                <div className="alterstabelle-buttons">
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setPkSubModal("maenner")}>
                    Alterstabelle Männer ({tabelleM.length} Stufen)
                  </button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setPkSubModal("frauen")}>
                    Alterstabelle Frauen ({tabelleF.length} Stufen)
                  </button>
                </div>
                {warnM && <div className="alterstabelle-warn">Achtung: Alterstabelle Männer hat weniger als 5 Einträge</div>}
                {warnF && <div className="alterstabelle-warn">Achtung: Alterstabelle Frauen hat weniger als 5 Einträge</div>}
              </>
            )}
          </>
        );
      }
      default:
        return null;
    }
  };

  const renderCard = (type) => {
    const entries = ratesByType[type] || [];
    const typeInfo = INSURANCE_TYPES[type];
    const isEditing = editing?.type === type;
    const todayStr = today();

    return (
      <div key={type} className="insurance-card">
        <div className="insurance-card-header">
          <h3>{typeInfo.label}</h3>
          {!isEditing && (
            <button className="btn btn-sm btn-secondary" onClick={() => handleNewEntry(type)}>
              + Neuer Satz
            </button>
          )}
        </div>

        {/* Edit form (inline) */}
        {isEditing && (
          <div className="insurance-form">
            <div className="form-row">
              <div className="form-group">
                <label>Gültig ab</label>
                <input
                  type="date"
                  value={editing.formData.gueltigAb}
                  onChange={(e) => handleFormChange("gueltigAb", e.target.value)}
                  disabled={!!editing.id}
                />
              </div>
              <div className="form-group">
                <label>Zahlungsrhythmus</label>
                <select
                  value={editing.formData.zahlungsrhythmus}
                  onChange={(e) => handleFormChange("zahlungsrhythmus", e.target.value)}
                >
                  {ZAHLUNGSRHYTHMUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {editing.formData.zahlungsrhythmus === "jaehrlich" && (
                <div className="form-group">
                  <label>Auszahlung Monat</label>
                  <select
                    value={editing.formData.jaehrlichEndMonat}
                    onChange={(e) => handleFormChange("jaehrlichEndMonat", parseInt(e.target.value))}
                  >
                    {MONTH_NAMES.map((name, i) => (
                      <option key={i + 1} value={i + 1}>{name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {renderFormFields()}
            <div className="form-group">
              <label>Bemerkung</label>
              <input
                type="text"
                value={editing.formData.bemerkung}
                onChange={(e) => handleFormChange("bemerkung", e.target.value)}
                placeholder="z.B. Neue Sätze 2027"
              />
            </div>
            <div className="insurance-form-actions">
              <button className="btn btn-sm btn-secondary" onClick={handleCancelEdit} disabled={saving}>
                Abbrechen
              </button>
              <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving || !editing.formData.gueltigAb}>
                {saving ? "..." : "Speichern"}
              </button>
            </div>
          </div>
        )}

        {/* Existing entries */}
        {entries.length === 0 && !isEditing && (
          <div className="insurance-empty">Keine Sätze erfasst</div>
        )}
        {entries.map((entry) => {
          const isPast = entry.gueltigAb < todayStr;
          const isFuture = entry.gueltigAb > todayStr;
          let statusLabel = "aktuell";
          if (isPast) {
            // Check if there's a more recent entry that's already current
            const moreRecent = entries.find((e) => e.gueltigAb > entry.gueltigAb && e.gueltigAb <= todayStr);
            if (moreRecent) statusLabel = "vergangen";
          }
          if (isFuture) statusLabel = "zukuenftig";

          return (
            <div key={entry.id} className={`insurance-entry${statusLabel === "vergangen" ? " past" : ""}`}>
              <div className="insurance-entry-header">
                <span className="insurance-entry-date">
                  Gültig ab {entry.gueltigAb}
                  {/* statusLabel dient auch als CSS-Klasse — Anzeige mit Umlaut via Map */}
                  <span className={`insurance-status-badge ${statusLabel}`}>{statusLabel === "zukuenftig" ? "zukünftig" : statusLabel}</span>
                </span>
                <div className="insurance-entry-actions">
                  {!isEditing && statusLabel !== "vergangen" && (
                    <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(entry)}>
                      Bearbeiten
                    </button>
                  )}
                  {isFuture && !isEditing && (
                    <button className="btn-icon btn-delete" onClick={() => handleDelete(entry)} title="Löschen">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="insurance-entry-rates">{renderRateFields(entry)}</div>
              <div className="insurance-entry-meta">
                {ZAHLUNGSRHYTHMUS_OPTIONS.find((o) => o.value === entry.zahlungsrhythmus)?.label || entry.zahlungsrhythmus}
                {entry.zahlungsrhythmus === "jaehrlich" && ` (${MONTH_NAMES[(entry.jaehrlichEndMonat || 12) - 1]})`}
                {entry.bemerkung && ` — ${entry.bemerkung}`}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="overlay" onClick={onClose}></div>
      <div className="insurance-settings-modal">
        <div className="modal-header">
          <h2>Versicherungen</h2>
          <button className="btn-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          {loading ? (
            <div className="loading-indicator">Lade Versicherungsdaten...</div>
          ) : (
            Object.keys(INSURANCE_TYPES).map((type) => renderCard(type))
          )}
        </div>
      </div>

      {/* PK Alterstabelle Sub-Modal */}
      {pkSubModal && editing && (
        <>
          <div className="overlay overlay-sub" onClick={() => setPkSubModal(null)}></div>
          <div className="pk-alterstabelle-modal">
            <div className="modal-header">
              <h3>Alterstabelle {pkSubModal === "maenner" ? "Männer" : "Frauen"}</h3>
              <button className="btn-close" onClick={() => setPkSubModal(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <table className="alterstabelle">
                <thead>
                  <tr>
                    <th>Alter</th>
                    <th>Gesamtsatz (%)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(editing.formData[pkSubModal === "maenner" ? "alterstabelleMaenner" : "alterstabelleFrauen"] || []).map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="number"
                          min="18"
                          max="70"
                          value={row.alter}
                          onChange={(e) => handleAltersTabelleChange(pkSubModal, idx, "alter", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={row.satz}
                          onChange={(e) => handleAltersTabelleChange(pkSubModal, idx, "satz", e.target.value)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn-icon btn-delete"
                          onClick={() => handleAltersTabelleRemove(pkSubModal, idx)}
                          title="Zeile entfernen"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="alterstabelle-actions">
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => handleAltersTabelleAdd(pkSubModal)}>
                  + Zeile
                </button>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => setPkSubModal(null)}>
                  Fertig
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default InsuranceSettingsModal;
